import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import ScrollReveal from "@/components/base/ScrollReveal";

export default function About() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="關於保險門神 | 品牌理念與專業團隊 | Insurance Guardian"
        description="了解保險門神的品牌理念—先守護再成長。我們不是單純銷售保險，而是站在客戶的人生與財務全局，協助檢視風險、整合資源、補足缺口，讓每一筆保費、每一項資產都能回到更安心的位置。"
        keywords="保險門神,品牌理念,財務顧問團隊,風險管理哲學,保險諮詢理念"
        canonicalPath="/about"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "關於保險門神 Insurance Guardian",
          "description": "了解保險門神的品牌理念—先守護再成長。我們不是單純銷售保險，而是站在客戶的人生與財務全局，協助檢視風險、整合資源、補足缺口。",
          "url": "__SITE_URL__/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          }
        }}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20flowing%20light%20streams%20on%20deep%20black%20canvas%2C%20subtle%20trust%20and%20professionalism%20motifs%2C%20elegant%20gold%20particles%20forming%20guardian%20shapes%2C%20premium%20brand%20aesthetic%20for%20insurance%20consulting%20company%2C%20sophisticated%20minimalist%20composition%20with%20warm%20golden%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=600&seq=201&orientation=landscape"
              alt="關於保險門神 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            <div className="inline-block border border-brand-border rounded-full px-5 py-2 mb-6">
              <span className="text-brand-gold text-[1rem] font-medium tracking-wide">
                關於我們
              </span>
            </div>
            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4 max-w-[680px]">
              關於保險門神
            </h1>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[720px]">
              保險不是商品堆疊，而是一套能守住人生、家庭、資產與企業的系統。
            </p>
          </div>
        </section>

        {/* Brand Philosophy */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <div className="max-w-[800px]">
              <ScrollReveal>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1 h-8 bg-brand-gold rounded-full" />
                  <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                    品牌理念
                  </h2>
                </div>
                <p className="text-brand-text-secondary text-[1.125rem] md:text-[1.1875rem] leading-[1.75]">
                  「保險門神」的核心不是單純銷售保險，而是站在客戶的人生與財務全局，協助檢視風險、整合資源、補足缺口，讓每一筆保費、每一項資產、每一個人生決策，都能回到更清楚、更安心的位置。
                </p>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* We Believe */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal className="text-center mb-14 md:mb-16">
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-4">
                我們相信
              </h2>
              <div className="w-[60px] h-[2px] bg-brand-gold mx-auto" />
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: "ri-shield-check-line",
                  title: "先守護，再成長",
                  desc: "沒有穩固的保障，財富成長容易被一次風險打斷。",
                },
                {
                  icon: "ri-search-line",
                  title: "先釐清，再規劃",
                  desc: "不是一開始就推薦商品，而是先了解家庭、資產、責任與目標。",
                },
                {
                  icon: "ri-settings-3-line",
                  title: "先系統，再執行",
                  desc: "真正有用的規劃，不是單點補強，而是保單、財務、退休、傳承與企業制度的整合。",
                },
              ].map((item, index) => (
                <ScrollReveal key={item.title} delay={index * 120}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-6 md:p-8 text-center h-full"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                      <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-brand-gold text-xl md:text-2xl">
                        <i className={item.icon} />
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.75]">
                      {item.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Advisor Role */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <div className="max-w-[800px]">
              <ScrollReveal>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1 h-8 bg-brand-gold rounded-full" />
                  <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                    我們是你的風險翻譯者與長期守門人
                  </h2>
                </div>
                <p className="text-brand-text-secondary text-[1.125rem] md:text-[1.1875rem] leading-[1.75]">
                  把看不懂的保單條款、複雜的資產配置、未來的教育金與退休金壓力、企業主的責任風險，轉換成你看得懂、能判斷、可執行的方案。
                </p>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-brand-card border-t border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="bg-brand-black border border-brand-border rounded-2xl p-8 md:p-12 text-center">
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-6">
                  讓保險門神，替你的下一階段把關。
                </h2>
                <a
                  href="https://lin.ee/o51JBHX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
                >
                  <i className="ri-line-fill" />
                  加入 LINE 預約諮詢
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
